import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { getReadingStorageKey } from "../../bible/readingStorageScope";

const STORAGE_READ_CHAPTERS = "bible_read_chapters";
export const STORAGE_PROGRESS_STYLE = "bible_progress_style"; // 'full' | 'minimal' | 'top_only' | 'hidden'

export const getReadChapters = (uid) => {
  try {
    const raw = localStorage.getItem(getReadingStorageKey(STORAGE_READ_CHAPTERS, uid));
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

export const saveChapterRead = (chapterId, read = true) => {
  try {
    const existing = getReadChapters();
    if (read) {
      existing[chapterId] = Date.now();
    } else {
      delete existing[chapterId];
    }
    localStorage.setItem(getReadingStorageKey(STORAGE_READ_CHAPTERS), JSON.stringify(existing));
    window.dispatchEvent(new Event("bible_read_status_changed"));
    return Boolean(existing[chapterId]);
  } catch (e) {
    return false;
  }
};

export const getProgressStyle = () => {
  try {
    const style = localStorage.getItem(STORAGE_PROGRESS_STYLE);
    if (["full", "minimal", "top_only", "hidden"].includes(style)) {
      return style;
    }
  } catch (e) {}
  return "full";
};

export const setProgressStylePref = (style) => {
  try {
    localStorage.setItem(STORAGE_PROGRESS_STYLE, style);
    window.dispatchEvent(new Event("bible_progress_style_changed"));
  } catch (e) {}
};

export const useChapterProgress = ({
  chapterId,
  verseNumbers = [],
  verseTextMap = {},
  sectionList = [],
  lang = "zh",
}) => {
  const totalVerses = verseNumbers.length || 0;
  const firstVerse = verseNumbers[0] ?? 1;
  const lastVerse = verseNumbers[verseNumbers.length - 1] ?? 1;

  const [currentVerse, setCurrentVerse] = useState(firstVerse);
  const [scrollPercent, setScrollPercent] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [scrollDirection, setScrollDirection] = useState(null);
  const [isCompleted, setIsCompleted] = useState(() => {
    return Boolean(getReadChapters()[chapterId]);
  });
  const [progressStyle, setProgressStyleState] = useState(getProgressStyle);

  const scrollTimerRef = useRef(null);
  const rafIdRef = useRef(null);
  const lastScrollYRef = useRef(
    typeof window !== "undefined" ? window.scrollY || 0 : 0
  );

  // Sync completion status when chapterId changes or event fires
  useEffect(() => {
    const syncStatus = () => {
      setIsCompleted(Boolean(getReadChapters()[chapterId]));
    };
    syncStatus();
    window.addEventListener("bible_read_status_changed", syncStatus);
    return () => {
      window.removeEventListener("bible_read_status_changed", syncStatus);
    };
  }, [chapterId]);

  // Sync display style preference
  useEffect(() => {
    const syncStyle = () => {
      setProgressStyleState(getProgressStyle());
    };
    window.addEventListener("bible_progress_style_changed", syncStyle);
    return () => {
      window.removeEventListener("bible_progress_style_changed", syncStyle);
    };
  }, []);

  // Track active verse and scroll percentage
  useEffect(() => {
    if (!totalVerses) return undefined;

    const handleScroll = () => {
      const liveY = window.scrollY || document.documentElement.scrollTop || 0;
      const delta = liveY - lastScrollYRef.current;
      if (Math.abs(delta) >= 1) {
        setScrollDirection(delta > 0 ? "down" : "up");
        lastScrollYRef.current = liveY;
      }

      setIsScrolling(true);
      if (scrollTimerRef.current) {
        window.clearTimeout(scrollTimerRef.current);
      }
      scrollTimerRef.current = window.setTimeout(() => {
        setIsScrolling(false);
      }, 800);

      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }

      rafIdRef.current = requestAnimationFrame(() => {
        const docElem = document.documentElement;
        const scrollY = window.scrollY || docElem.scrollTop;
        const scrollHeight = docElem.scrollHeight;
        const clientHeight = window.innerHeight;
        const maxScroll = Math.max(1, scrollHeight - clientHeight);

        const percent = Math.min(
          100,
          Math.max(0, Math.round((scrollY / maxScroll) * 100))
        );
        setScrollPercent(percent);

        // Check if reached the end of the chapter (>95%) to auto-mark read
        if (percent >= 96 && chapterId) {
          const currentReads = getReadChapters();
          if (!currentReads[chapterId]) {
            saveChapterRead(chapterId, true);
          }
        }

        // Determine current reading verse
        if (scrollY <= 50) {
          setCurrentVerse(firstVerse);
          return;
        }

        if (scrollY + clientHeight >= scrollHeight - 40) {
          setCurrentVerse(lastVerse);
          return;
        }

        // Focal line: where the reader's eye typically rests (~28% of the viewport)
        const focalLine = clientHeight * 0.28;
        const verseNodes = document.querySelectorAll("[data-verse]");
        if (!verseNodes || verseNodes.length === 0) return;

        let bestVerse = firstVerse;
        let bestDistance = Infinity;

        verseNodes.forEach((node) => {
          const rect = node.getBoundingClientRect();
          // Verse top relative to focal line
          const dist = rect.top - focalLine;
          // If the verse is at or above the focal line, or slightly below it
          if (rect.top <= focalLine && rect.bottom >= focalLine) {
            const vNum = Number(node.getAttribute("data-verse"));
            if (Number.isFinite(vNum)) {
              bestVerse = vNum;
              bestDistance = 0;
            }
          } else if (dist < 0 && Math.abs(dist) < bestDistance) {
            const vNum = Number(node.getAttribute("data-verse"));
            if (Number.isFinite(vNum)) {
              bestVerse = vNum;
              bestDistance = Math.abs(dist);
            }
          }
        });

        setCurrentVerse(bestVerse);
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    // Initial evaluation
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimerRef.current) window.clearTimeout(scrollTimerRef.current);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, [chapterId, firstVerse, lastVerse, totalVerses]);

  // Calculate estimated reading time remaining
  const estimatedMinutesRemaining = useMemo(() => {
    if (!totalVerses || scrollPercent >= 98 || currentVerse === lastVerse) {
      return 0;
    }

    let remainingCharsOrWords = 0;
    const isEn = lang.startsWith("en");

    for (let v = currentVerse; v <= lastVerse; v++) {
      const text = verseTextMap[String(v)] || "";
      if (isEn) {
        const words = text.trim().split(/\s+/).filter(Boolean);
        remainingCharsOrWords += words.length;
      } else {
        remainingCharsOrWords += text.replace(/\s+/g, "").length;
      }
    }

    const readingSpeed = isEn ? 220 : 350; // words per min for EN, chars per min for ZH
    const mins = Math.ceil(remainingCharsOrWords / readingSpeed);
    return Math.max(1, mins);
  }, [currentVerse, lang, lastVerse, scrollPercent, totalVerses, verseTextMap]);

  // Current active section heading based on current verse
  const currentSection = useMemo(() => {
    if (!sectionList || sectionList.length === 0) return null;
    let match = null;
    for (const section of sectionList) {
      if (section.verse <= currentVerse) {
        match = section;
      } else {
        break;
      }
    }
    return match;
  }, [currentVerse, sectionList]);

  // Toggle chapter completed state
  const toggleCompleted = useCallback(() => {
    const nextVal = !isCompleted;
    saveChapterRead(chapterId, nextVal);
    setIsCompleted(nextVal);
  }, [chapterId, isCompleted]);

  // Smooth scroll to a target verse
  const scrollToVerse = useCallback((targetVerse) => {
    const targetNode = document.querySelector(`[data-verse="${targetVerse}"]`);
    if (targetNode) {
      targetNode.scrollIntoView({ behavior: "smooth", block: "center" });

      // Subtle visual ping to highlight the verse
      targetNode.animate(
        [
          { filter: "brightness(1)" },
          { filter: "brightness(1.18)" },
          { filter: "brightness(1)" },
        ],
        { duration: 700, easing: "ease-out" }
      );
    }
  }, []);

  // Smooth scroll to top of chapter
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Change progress style preference
  const setStyle = useCallback((style) => {
    setProgressStylePref(style);
    setProgressStyleState(style);
  }, []);

  return {
    currentVerse,
    totalVerses,
    firstVerse,
    lastVerse,
    scrollPercent,
    isScrolling,
    scrollDirection,
    isCompleted,
    progressStyle,
    estimatedMinutesRemaining,
    currentSection,
    toggleCompleted,
    scrollToVerse,
    scrollToTop,
    setStyle,
  };
};

export default useChapterProgress;
